import React, { useState, useEffect, useRef } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField,
  MenuItem,
  IconButton,
  CircularProgress,
  Typography,
  Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { OffersAPI } from 'api_inbusiness';
import { useUser } from 'hooks/useUser';
import styles from './CreateOfferModal.module.scss';

const CATEGORIES = [
  'Servicii',
  'Produse',
  'Consultanță',
  'Educație',
  'Tehnologie',
  'Sănătate',
  'Altele',
];

const EditOfferModal = ({ open, onClose, onSuccess, offer }) => {
  const { user } = useUser();
  const fileInputRef = useRef();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    discount: '',
    category: '',
    validUntil: '',
    terms: '',
    companyId: '',
    isActive: true,
  });

  // Image state
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [hasExistingImage, setHasExistingImage] = useState(false);

  // Get user's companies from the user object
  const companies = user?.companies?.filter(c => !c.isDeleted) || [];

  // Populate form with offer data when modal opens
  useEffect(() => {
    if (open && offer) {
      setFormData({
        title: offer.title || '',
        description: offer.description || '',
        discount: offer.discount || '',
        category: offer.category || '',
        validUntil: offer.validUntil ? offer.validUntil.split('T')[0] : '',
        terms: offer.terms || '',
        companyId: offer.companyId || '',
        isActive: offer.isActive ?? true,
      });
      
      // Reset image state
      setSelectedImage(null);
      
      // Check if offer has existing image
      checkExistingImage(offer.id);
    }
  }, [open, offer]);

  const checkExistingImage = async (offerId) => {
    const res = await OffersAPI.checkImageExists(offerId);
    if (res.ok && res.exists) {
      setHasExistingImage(true);
      setImagePreview(OffersAPI.getImageUrl(offerId, Date.now()));
    } else {
      setHasExistingImage(false);
      setImagePreview(null);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle image selection
  const handleImageSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Tip de fișier invalid. Sunt acceptate doar JPEG, PNG, WebP și GIF.");
        return;
      }
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Imaginea nu poate depăși 5MB.");
        return;
      }
      
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));
      setHasExistingImage(false);
    }
  };

  // Remove selected/existing image
  const handleRemoveImage = async () => {
    if (hasExistingImage && offer?.id) {
      // Delete existing image from server
      await OffersAPI.deleteImage(offer.id);
    }
    
    setSelectedImage(null);
    setImagePreview(null);
    setHasExistingImage(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error('Titlul este obligatoriu!');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Descrierea este obligatorie!');
      return;
    }

    setLoading(true);
    const res = await OffersAPI.update(offer.id, {
      title: formData.title,
      description: formData.description,
      discount: formData.discount || null,
      category: formData.category || null,
      validUntil: formData.validUntil || null,
      terms: formData.terms || null,
      companyId: formData.companyId || null,
      isActive: formData.isActive,
    });

    if (res.ok) {
      // Upload new image if selected
      if (selectedImage) {
        await OffersAPI.uploadImage(offer.id, selectedImage);
      }
      
      toast.success('Oferta a fost actualizată cu succes!');
      onSuccess?.();
    } else {
      toast.error(res.error || 'Eroare la actualizarea ofertei');
    }
    
    setLoading(false);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        className: styles.dialog
      }}
    >
      <DialogTitle className={styles.title}>
        Editează Oferta
        <IconButton onClick={onClose} className={styles.closeButton}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={styles.content}>
        {/* Image Upload Section */}
        <Box className={styles.imageUploadSection}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Imagine Ofertă
          </Typography>
          
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageSelect}
            accept="image/jpeg,image/png,image/webp,image/gif"
            style={{ display: 'none' }}
          />
          
          {imagePreview ? (
            <div className={styles.imagePreviewContainer}>
              <img src={imagePreview} alt="Preview" className={styles.imagePreview} />
              <IconButton 
                className={styles.removeImageBtn}
                onClick={handleRemoveImage}
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            </div>
          ) : (
            <Button
              variant="outlined"
              startIcon={<AddPhotoAlternateIcon />}
              onClick={() => fileInputRef.current?.click()}
              className={styles.uploadButton}
              fullWidth
            >
              Adaugă Imagine
            </Button>
          )}
        </Box>

        <TextField
          name="title"
          label="Titlu *"
          fullWidth
          value={formData.title}
          onChange={handleChange}
          margin="normal"
        />

        <TextField
          name="description"
          label="Descriere *"
          fullWidth
          multiline
          rows={3}
          value={formData.description}
          onChange={handleChange}
          margin="normal"
        />

        <div className={styles.row}>
          <TextField
            name="discount"
            label="Reducere"
            placeholder="ex: 20%, 50 RON, Gratuit"
            fullWidth
            value={formData.discount}
            onChange={handleChange}
            margin="normal"
          />

          <TextField
            name="category"
            label="Categorie"
            select
            fullWidth
            value={formData.category}
            onChange={handleChange}
            margin="normal"
          >
            <MenuItem value="">Fără categorie</MenuItem>
            {CATEGORIES.map((cat) => (
              <MenuItem key={cat} value={cat}>{cat}</MenuItem>
            ))}
          </TextField>
        </div>

        <div className={styles.row}>
          <TextField
            name="validUntil"
            label="Valabil până la"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={formData.validUntil}
            onChange={handleChange}
            margin="normal"
          />

          <TextField
            name="isActive"
            label="Status"
            select
            fullWidth
            value={formData.isActive}
            onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.value === 'true' }))}
            margin="normal"
          >
            <MenuItem value="true">Activă</MenuItem>
            <MenuItem value="false">Inactivă</MenuItem>
          </TextField>
        </div>

        {companies.length > 0 && (
          <TextField
            name="companyId"
            label="Companie"
            select
            fullWidth
            value={formData.companyId}
            onChange={handleChange}
            margin="normal"
          >
            <MenuItem value="">Fără companie</MenuItem>
            {companies.map((comp) => (
              <MenuItem key={comp.id} value={comp.id}>{comp.name}</MenuItem>
            ))}
          </TextField>
        )}

        <TextField
          name="terms"
          label="Termeni și condiții"
          fullWidth
          multiline
          rows={2}
          value={formData.terms}
          onChange={handleChange}
          margin="normal"
          placeholder="Opțional: condiții speciale pentru această ofertă"
        />
      </DialogContent>

      <DialogActions className={styles.actions}>
        <Button onClick={onClose} disabled={loading}>
          Anulează
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          disabled={loading}
          className={styles.submitButton}
        >
          {loading ? <CircularProgress size={20} /> : 'Salvează Modificările'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditOfferModal;

